import { useState, useEffect } from 'react';
import { Search, X, Filter, ChevronDown, ChevronUp } from 'lucide-react';
import toast from 'react-hot-toast';
import { getCirugias, Cirugia, getHospitales, getTiposCirugia, getTipoCirugia, getHospital } from '../../services/cirugiasService';
import medicoVfpService, { PacienteVfp } from '../../services/medicoVfpService';
import DetalleCirugiaModal from './DetalleCirugiaModal';
import SearchableSelect from '../common/SearchableSelect';

export default function BusquedaCirugias() {
  const [cirugias, setCirugias] = useState<Cirugia[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedCirugia, setSelectedCirugia] = useState<Cirugia | null>(null);
  const [showDetalleModal, setShowDetalleModal] = useState(false);

  // Filtros
  const [padecimiento, setPadecimiento] = useState('');
  const [estado, setEstado] = useState('');
  const [hospitalId, setHospitalId] = useState('');
  const [tipoCirugiaId, setTipoCirugiaId] = useState('');
  const [sala, setSala] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [pacienteId, setPacienteId] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  // Catálogos
  const [hospitales, setHospitales] = useState<any[]>([]);
  const [tiposCirugia, setTiposCirugia] = useState<any[]>([]);
  const [pacientesOptions, setPacientesOptions] = useState<{ value: number; label: string; subtitle?: string }[]>([]);

  // Mapas de caché para datos relacionados
  const [pacientesMap, setPacientesMap] = useState<Record<number, PacienteVfp>>({});
  const [tiposCirugiaMap, setTiposCirugiaMap] = useState<Record<number, any>>({});
  const [hospitalesMap, setHospitalesMap] = useState<Record<number, any>>({});

  useEffect(() => {
    loadCatalogos();
    loadCirugias();
  }, []);

  const loadCatalogos = async () => {
    try {
      const [hospitalesData, tiposData, pacientesData] = await Promise.all([
        getHospitales({ isActive: true }),
        getTiposCirugia({ isActive: true }),
        medicoVfpService.getPacientesVfp({ take: 1000 }),
      ]) as [any, any, any];

      const hospitalesList = Array.isArray(hospitalesData)
        ? hospitalesData
        : Array.isArray(hospitalesData?.data)
        ? hospitalesData.data
        : (hospitalesData?.data?.data || []);

      const tiposList = Array.isArray(tiposData)
        ? tiposData
        : Array.isArray(tiposData?.data)
        ? tiposData.data
        : (tiposData?.data?.data || []);

      setHospitales(hospitalesList);
      setTiposCirugia(tiposList);

      const pacientesList = Array.isArray(pacientesData)
        ? pacientesData
        : Array.isArray(pacientesData?.data)
        ? pacientesData.data
        : (pacientesData?.data?.data || []);
      setPacientesOptions(
        pacientesList.map((p: PacienteVfp) => ({
          value: p.clave,
          label: [p.apellido1, p.apellido2, p.nombre].filter(Boolean).join(' ').trim(),
          subtitle: `Clave: ${p.clave}`,
        }))
      );
    } catch (error) {
      console.error('Error al cargar catálogos:', error);
    }
  };

  const loadCirugias = async () => {
    try {
      setLoading(true);
      const params: any = {
        take: 50,
        isActive: true,
      };

      if (padecimiento) params.padecimiento = padecimiento;
      if (estado) params.estado = estado;
      if (hospitalId) params.hospitalId = parseInt(hospitalId);
      if (tipoCirugiaId) params.tipoCirugiaId = parseInt(tipoCirugiaId);
      if (sala) params.sala = sala;
      if (fechaDesde) params.fechaDesde = fechaDesde;
      if (fechaHasta) params.fechaHasta = fechaHasta;
      if (pacienteId) params.pacienteId = pacienteId;

      const response = await getCirugias(params);
      let data: Cirugia[] = [];
      let totalItems = 0;

      if (Array.isArray(response)) {
        data = response;
        totalItems = response.length;
      } else {
        data = Array.isArray(response?.data)
          ? response.data
          : (response?.data?.data || []);

        totalItems =
          typeof response?.total === 'number'
            ? response.total
            : typeof response?.data?.total === 'number'
            ? response.data.total
            : data.length;
      }

      setCirugias(data);
      setTotal(totalItems);

      // Cargar datos relacionados
      const newPacientesMap: Record<number, PacienteVfp> = { ...pacientesMap };
      const newTiposCirugiaMap: Record<number, any> = { ...tiposCirugiaMap };
      const newHospitalesMap: Record<number, any> = { ...hospitalesMap };

      for (const cirugia of data) {
        // Cargar paciente si no está en caché
        if (cirugia.pacienteId && !newPacientesMap[cirugia.pacienteId]) {
          try {
            const paciente = await medicoVfpService.getPacienteVfp(cirugia.pacienteId);
            newPacientesMap[cirugia.pacienteId] = paciente;
          } catch (error) {
            console.error(`Error al cargar paciente ${cirugia.pacienteId}:`, error);
          }
        }

        // Cargar tipo de cirugía si no está en caché
        if (cirugia.tipoCirugiaId && !newTiposCirugiaMap[cirugia.tipoCirugiaId]) {
          try {
            const tipo = await getTipoCirugia(cirugia.tipoCirugiaId);
            newTiposCirugiaMap[cirugia.tipoCirugiaId] = tipo;
          } catch (error) {
            console.error(`Error al cargar tipo de cirugía ${cirugia.tipoCirugiaId}:`, error);
          }
        }

        // Cargar hospital si no está en caché
        if (cirugia.hospitalId && !newHospitalesMap[cirugia.hospitalId]) {
          try {
            const hospital = await getHospital(cirugia.hospitalId);
            newHospitalesMap[cirugia.hospitalId] = hospital;
          } catch (error) {
            console.error(`Error al cargar hospital ${cirugia.hospitalId}:`, error);
          }
        }
      }

      setPacientesMap(newPacientesMap);
      setTiposCirugiaMap(newTiposCirugiaMap);
      setHospitalesMap(newHospitalesMap);
    } catch (error) {
      console.error('Error al cargar cirugías:', error);
      toast.error('Error al cargar cirugías');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadCirugias();
  };

  const handleClearFilters = () => {
    setPadecimiento('');
    setEstado('');
    setHospitalId('');
    setTipoCirugiaId('');
    setSala('');
    setFechaDesde('');
    setFechaHasta('');
    setPacienteId(0);
  };

  const handleCirugiaClick = (cirugia: Cirugia) => {
    setSelectedCirugia(cirugia);
    setShowDetalleModal(true);
  };

  const formatFecha = (fecha: string) => {
    const [year, month, day] = fecha.split('T')[0].split('-').map(Number);
    const parts = new Intl.DateTimeFormat('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).formatToParts(new Date(year, month - 1, day));
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
    const mon = get('month').replace('.', '').toUpperCase();
    return `${get('day')} ${mon} ${get('year')}`;
  };

  const getEstadoBadgeColor = (estado: string) => {
    switch (estado) {
      case 'prospecto':
        return 'bg-yellow-100 text-yellow-800';
      case 'confirmada':
        return 'bg-green-100 text-green-800';
      case 'en_proceso':
        return 'bg-blue-100 text-blue-800';
      case 'concluida':
        return 'bg-gray-100 text-gray-800';
      case 'cancelada':
        return 'bg-red-100 text-red-800';
      case 'pospuesta':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div>
      {/* Filtros */}
      <div className="mb-6">
        <div className="p-4 bg-white rounded-lg border border-gray-200">
          {/* Toggle button */}
          <div className="flex items-center justify-between mb-0">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors"
            >
              <Filter className="w-4 h-4" />
              Filtros
              {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showFilters && (
              <div className="flex gap-2">
                <button
                  onClick={handleClearFilters}
                  className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-1.5 text-sm"
                >
                  <X className="w-4 h-4" />
                  Limpiar
                </button>
                <button
                  onClick={handleSearch}
                  className="px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1.5 text-sm"
                >
                  <Search className="w-4 h-4" />
                  Buscar
                </button>
              </div>
            )}
          </div>

          {showFilters && (
            <div className="mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                {/* Paciente */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Paciente
                  </label>
                  <SearchableSelect
                    options={pacientesOptions}
                    value={pacienteId}
                    onChange={setPacienteId}
                    placeholder="Buscar paciente..."
                  />
                </div>

                {/* Padecimiento */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Padecimiento
                  </label>
                  <input
                    type="text"
                    value={padecimiento}
                    onChange={(e) => setPadecimiento(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="Buscar padecimiento..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Tipo de Cirugía */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Cirugía
                  </label>
                  <select
                    value={tipoCirugiaId}
                    onChange={(e) => setTipoCirugiaId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Todos</option>
                    {tiposCirugia.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Hospital */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hospital
                  </label>
                  <select
                    value={hospitalId}
                    onChange={(e) => setHospitalId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Todos</option>
                    {hospitales.map((h) => (
                      <option key={h.id} value={h.id}>
                        {h.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Sala Quirúrgica */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sala Quirúrgica
                  </label>
                  <input
                    type="text"
                    value={sala}
                    onChange={(e) => setSala(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="Ej: Q12, Q56..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Estado */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estado
                  </label>
                  <select
                    value={estado}
                    onChange={(e) => setEstado(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Todos</option>
                    <option value="prospecto">Prospecto</option>
                    <option value="confirmada">Confirmada</option>
                    <option value="en_proceso">En Proceso</option>
                    <option value="concluida">Concluida</option>
                    <option value="cancelada">Cancelada</option>
                    <option value="pospuesta">Pospuesta</option>
                  </select>
                </div>

                {/* Fecha Desde */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha Desde
                  </label>
                  <input
                    type="date"
                    value={fechaDesde}
                    onChange={(e) => setFechaDesde(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Fecha Hasta */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha Hasta
                  </label>
                  <input
                    type="date"
                    value={fechaHasta}
                    onChange={(e) => setFechaHasta(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>


      {/* Resultados */}
      <div className="mb-4">
        <p className="text-sm text-gray-600">
          {total} resultado{total !== 1 ? 's' : ''} encontrado{total !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Tabla de resultados */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : cirugias.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <Search className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No se encontraron cirugías</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Padecimiento
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Paciente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo de Cirugía
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hospital
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hora
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sala
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pago
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {cirugias.map((cirugia) => (
                  <tr
                    key={cirugia.id}
                    onClick={() => handleCirugiaClick(cirugia)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatFecha(cirugia.fechaProgramada)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {cirugia.padecimiento}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {pacientesMap[cirugia.pacienteId]
                        ? `${pacientesMap[cirugia.pacienteId].nombre || ''} ${pacientesMap[cirugia.pacienteId].apellido1 || ''}`.trim()
                        : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {tiposCirugiaMap[cirugia.tipoCirugiaId]?.nombre || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {hospitalesMap[cirugia.hospitalId]?.nombre || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {cirugia.horaInicio || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {cirugia.salaQuirurgica || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getEstadoBadgeColor(
                          cirugia.estado
                        )}`}
                      >
                        {cirugia.estado}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {cirugia.tipoPago === 'seguro' ? 'Con Seguro' : 'Sin Seguro'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal de detalle */}
      {showDetalleModal && selectedCirugia && (
        <DetalleCirugiaModal
          cirugia={selectedCirugia}
          onClose={() => {
            setShowDetalleModal(false);
            setSelectedCirugia(null);
          }}
          onUpdate={loadCirugias}
        />
      )}
    </div>
  );
}
